// src/contexts/IdleTimerContext.js
import React, { createContext, useContext, useEffect, useState, useRef, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, Typography } from '@mui/material';
import { useUser } from './UserContext';
import { useLocation } from 'react-router-dom';

// Idle auto-logout window, in minutes. Configured per business via
// UserGroup.ui_config.session_timeout_minutes (see UpdateSessionTimeoutView).
// A business that never sets it simply has no key, and falls back to 10.
export const DEFAULT_IDLE_TIMEOUT_MINUTES = 10;
export const MIN_IDLE_TIMEOUT_MINUTES = 1;
export const MAX_IDLE_TIMEOUT_MINUTES = 480;   // 8 hours

const WARNING_DURATION_MS = 60 * 1000;   // 60 seconds warning (not configurable)
const WARNING_DURATION_SEC = WARNING_DURATION_MS / 1000;

/**
 * Coerce a stored config value into a usable number of minutes.
 *
 * Deliberately asymmetric. Garbage and sub-minimum values fall back to the
 * DEFAULT rather than clamping to MIN, because the admin raw-JSON editor can
 * write 0 straight to ui_config, bypassing the API's validation — and a
 * one-minute logout loop is one a manager cannot sit still long enough to fix
 * from the settings page. Oversized values clamp down, since the intent there
 * ("a very long session") is unambiguous.
 */
export const sanitizeIdleTimeoutMinutes = (raw) => {
  // Must precede Number(): Number(true) === 1 would read as a 1-minute timeout.
  if (raw === null || raw === undefined || typeof raw === 'boolean') return DEFAULT_IDLE_TIMEOUT_MINUTES;
  const n = Math.floor(Number(typeof raw === 'string' ? raw.trim() : raw));
  if (!Number.isFinite(n)) return DEFAULT_IDLE_TIMEOUT_MINUTES;          // NaN, "abc", {}, Infinity
  if (n < MIN_IDLE_TIMEOUT_MINUTES) return DEFAULT_IDLE_TIMEOUT_MINUTES; // 0, -5, 0.5
  if (n > MAX_IDLE_TIMEOUT_MINUTES) return MAX_IDLE_TIMEOUT_MINUTES;     // clamp down
  return n;
};

/** Hebrew needs three grammatical cases for a minute count. */
const formatMinutesHe = (minutes) => {
  if (minutes === 1) return 'דקה אחת';
  if (minutes === 2) return 'שתי דקות';
  return `${minutes} דקות`;
};

const formatSecondsHe = (seconds) => {
  if (seconds === 1) return 'שנייה אחת';
  if (seconds === 2) return 'שתי שניות';
  return `${seconds} שניות`;
};

const IdleTimerContext = createContext();

export const IdleTimerProvider = ({ children }) => {
  /* ---------- ALL hooks must come first (React rule) ---------- */
  const { userProfile, logout } = useUser();
  const { pathname } = useLocation();
  const [warningOpen, setWarningOpen] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_DURATION_SEC);
  const idleTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  
  // New refs for timestamp tracking
  const lastActivityRef = useRef(Date.now());
  const warningStartTimeRef = useRef(null);

  /* ---------- Per-business idle window ---------- */
  // Depends on the scalar rather than the whole userProfile object, so it does
  // not recompute on every profile refetch.
  const idleTimeoutMinutes = useMemo(
    () => sanitizeIdleTimeoutMinutes(userProfile?.ui_config?.session_timeout_minutes),
    [userProfile?.ui_config?.session_timeout_minutes],
  );

  // Timer callbacks read the timeout through this ref, never through the
  // closure. checkIdleTimeout re-schedules *itself* via setTimeout, the backup
  // setInterval captures it from the render its effect ran in, and the activity
  // listeners are registered once per effect run (capture phase) and hold that
  // run's closures for their whole lifetime. A ref keeps all of them live
  // without re-registering every listener whenever the config changes.
  const idleTimeoutMsRef = useRef(idleTimeoutMinutes * 60 * 1000);
  // Must be an effect, not a render-body write, to stay correct under StrictMode.
  useEffect(() => {
    idleTimeoutMsRef.current = idleTimeoutMinutes * 60 * 1000;
  }, [idleTimeoutMinutes]);

  /* ---------- Check if we're on login page ---------- */
  const isLoginPage = pathname === '/login';

  /* ---------- Skip idle timer entirely for installed PWA ("mobile") sessions ---------- */
  // Mobile (installed PWA) sessions should behave like a native app: stay
  // logged in until the refresh token expires. Web sessions keep the existing
  // 10-min idle + 60-sec warning behavior.
  const isMobileSession = (() => {
    try {
      return localStorage.getItem('device_type') === 'mobile';
    } catch (e) {
      return false;
    }
  })();

  const clearAllTimers = () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const checkIdleTimeout = () => {
    // Don't check if on login page or no user
    if (isLoginPage || !userProfile) return;
    
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    
    // If warning is already open, let the countdown handle it
    if (warningOpen) return;
    
    // Check if total timeout (idle + warning) has been exceeded
    if (timeSinceLastActivity >= (idleTimeoutMsRef.current + WARNING_DURATION_MS)) {
      // Time has expired completely - logout immediately
      clearAllTimers();
      setWarningOpen(false);
      logout();
      return;
    }
    
    // Check if idle timeout has been exceeded
    if (timeSinceLastActivity >= idleTimeoutMsRef.current) {
      // Show warning but adjust the countdown based on how much time has already passed
      const timeOverIdle = timeSinceLastActivity - idleTimeoutMsRef.current;
      const remainingWarningTime = WARNING_DURATION_MS - timeOverIdle;
      
      if (remainingWarningTime > 0) {
        setWarningOpen(true);
        warningStartTimeRef.current = now - timeOverIdle; // Backdate the warning start
        setCountdown(Math.ceil(remainingWarningTime / 1000));
        startCountdown();
      } else {
        // Should not happen, but just in case
        clearAllTimers();
        setWarningOpen(false);
        logout();
      }
    } else {
      // Schedule next check for when timeout will be reached
      const timeUntilIdle = idleTimeoutMsRef.current - timeSinceLastActivity;
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        checkIdleTimeout();
      }, Math.min(timeUntilIdle + 100, 5000)); // Check at least every 5 seconds
    }
  };

  const resetTimers = () => {
    // Clear warning immediately
    setWarningOpen(false);
    setCountdown(WARNING_DURATION_SEC);
    warningStartTimeRef.current = null;
    clearAllTimers();
    
    // Don't set timer if on login page
    if (!isLoginPage && userProfile) {
      // Record current time as last activity
      lastActivityRef.current = Date.now();
      // Schedule the idle check
      idleTimerRef.current = setTimeout(() => {
        checkIdleTimeout();
      }, idleTimeoutMsRef.current);
    }
  };

  const startCountdown = () => {
    setCountdown(WARNING_DURATION_SEC);
    
    countdownIntervalRef.current = setInterval(() => {
      if (!warningStartTimeRef.current) {
        clearInterval(countdownIntervalRef.current);
        return;
      }
      
      const now = Date.now();
      const warningElapsed = now - warningStartTimeRef.current;
      const remainingSeconds = Math.ceil((WARNING_DURATION_MS - warningElapsed) / 1000);
      
      if (remainingSeconds <= 0) {
        clearAllTimers();
        setWarningOpen(false);
        logout();
      } else {
        setCountdown(remainingSeconds);
      }
    }, 200); // Update 5 times per second for smooth countdown
  };

  const handleActivity = () => {
    // Don't track activity on login page or if no user
    if (!userProfile || isLoginPage) return;
    
    // Update last activity timestamp
    lastActivityRef.current = Date.now();
    
    // If warning is showing, reset everything
    if (warningOpen) {
      // Clear the warning start time first
      warningStartTimeRef.current = null;
      resetTimers();
    }
  };

  // Handle page visibility changes (tab focus/blur)
  const handleVisibilityChange = () => {
    if (!document.hidden && userProfile && !isLoginPage) {
      // Page became visible, check if we should timeout or logout immediately
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;
      
      // If total time exceeded, logout immediately
      if (timeSinceLastActivity >= (idleTimeoutMsRef.current + WARNING_DURATION_MS)) {
        clearAllTimers();
        setWarningOpen(false);
        logout();
      } else {
        // Otherwise check normally
        checkIdleTimeout();
      }
    }
  };

  // Handle window focus (browser minimize/restore)
  const handleWindowFocus = () => {
    if (userProfile && !isLoginPage) {
      // Window regained focus, check if we should timeout or logout immediately
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;
      
      // If total time exceeded, logout immediately
      if (timeSinceLastActivity >= (idleTimeoutMsRef.current + WARNING_DURATION_MS)) {
        clearAllTimers();
        setWarningOpen(false);
        logout();
      } else {
        // Otherwise check normally
        checkIdleTimeout();
      }
    }
  };

  useEffect(() => {
    // Don't set up timers if no user, on login page, or on a mobile (PWA) session
    if (!userProfile || isLoginPage || isMobileSession) {
      clearAllTimers();
      setWarningOpen(false);
      return;
    }

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    // Use capture phase to catch events before they might be stopped by the dialog
    events.forEach(evt => window.addEventListener(evt, handleActivity, true));
    
    // Also add to document for better keyboard capture
    document.addEventListener('keydown', handleActivity, true);
    document.addEventListener('keyup', handleActivity, true);
    document.addEventListener('keypress', handleActivity, true);
    
    // Add visibility and focus listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    
    // Initialize
    resetTimers();

    return () => {
      events.forEach(evt => window.removeEventListener(evt, handleActivity, true));
      document.removeEventListener('keydown', handleActivity, true);
      document.removeEventListener('keyup', handleActivity, true);
      document.removeEventListener('keypress', handleActivity, true);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      clearAllTimers();
    };
  // idleTimeoutMs is intentionally NOT a dependency: it is read through
  // idleTimeoutMsRef inside the callbacks, so adding it here would tear down and
  // re-register every listener (and re-stamp lastActivityRef) on each config change.
  }, [userProfile, isLoginPage]); // Add isLoginPage to dependencies

  // Clear timers when navigating to login page
  useEffect(() => {
    if (isLoginPage) {
      clearAllTimers();
      setWarningOpen(false);
    }
  }, [isLoginPage]);

  // Periodic check as backup (handles edge cases like system sleep)
  useEffect(() => {
    if (!userProfile || isLoginPage || isMobileSession) return;
    
    const backupCheck = setInterval(() => {
      // Quick check for immediate logout
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;
      
      if (timeSinceLastActivity >= (idleTimeoutMsRef.current + WARNING_DURATION_MS)) {
        clearAllTimers();
        setWarningOpen(false);
        logout();
      } else if (!warningOpen) {
        checkIdleTimeout();
      }
    }, 5000); // Check every 5 seconds
    
    return () => clearInterval(backupCheck);
  // See above: the idle window is read through idleTimeoutMsRef, not a dependency.
  }, [userProfile, isLoginPage, warningOpen]);

  return (
    <IdleTimerContext.Provider value={{ idleTimeoutMinutes, warningSeconds: WARNING_DURATION_SEC }}>
      {children}
      
      {/* Don't show dialog on login page */}
      {!isLoginPage && (
        <Dialog 
          open={warningOpen} 
          onClose={() => {}} 
          disableEscapeKeyDown
          // Add click handler to dialog itself
          onClick={handleActivity}
          onMouseMove={handleActivity}
          onKeyDown={handleActivity}
          // Ensure dialog content is focusable for keyboard events
          PaperProps={{
            tabIndex: -1,
            onKeyDown: handleActivity,
          }}
        >
          <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
            התראה על סיום פעילות
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold', mb: 2 }}>
              לא זוהתה פעילות במשך {formatMinutesHe(idleTimeoutMinutes)}.
              <br />
              אתה עומד להתנתק בעוד {formatSecondsHe(countdown)}.
            </Typography>
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
              כדי להמשיך בפעילות, הזז את העכבר או הקש על מקש.
            </Typography>
          </DialogContent>
        </Dialog>
      )}
    </IdleTimerContext.Provider>
  );
};

export const useIdleTimer = () => useContext(IdleTimerContext);